import { FIELD_KEYS, COMPLEX_FIELD_KEYS, MODEL_KEYS, buildComplexFieldKey } from "@/constants";
import { KeyPerson } from "@/types/KeyPerson";
import { FieldSchemaFormBuilderV2, SchemaFactoryV2 } from "@ucl/library";
import {
  EmploymentInformationComplexField,
  DigitalBankingInformationComplexField,
  DebitCardInformationComplexField,
  GovernmentIdInformationComplexField,
  PersonInformationComplexField,
  SecurityQuestionsInformationComplexField,
} from "../ComplexFields";
import { IdentificationDocumentsComplexField } from "../ComplexFields/IdentificationDocumentsComplexField";
import { FinancialApplicationFormSectionContextType } from "@/hooks/useFinancialApplicationFormSectionValidation";

export const createKeyPersonFormBuilder = (
  schemaFactory: SchemaFactoryV2,
  executionContextType: FinancialApplicationFormSectionContextType
): FieldSchemaFormBuilderV2<KeyPerson> => {
  const applicantRequiredFields: string[] = [
    FIELD_KEYS.KEY_PERSON.BENEFICIAL_OWNER_PERCENTAGE,

    // Person Information Complex Fields
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.PERSON_INFORMATION,
      COMPLEX_FIELD_KEYS.PERSON_INFORMATION.FIRST_NAME
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.PERSON_INFORMATION,
      COMPLEX_FIELD_KEYS.PERSON_INFORMATION.LAST_NAME
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.PERSON_INFORMATION,
      COMPLEX_FIELD_KEYS.PERSON_INFORMATION.PHONE_NUMBER
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.PERSON_INFORMATION,
      COMPLEX_FIELD_KEYS.PERSON_INFORMATION.EMAIL_ADDRESS
    ),

    // Government ID Information Complex Fields
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
      COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.CAN_PROVIDE_GOVERNMENT_ID_INFORMATION
    ),

    // Digital Banking Information Complex Fields
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.DIGITAL_BANKING_INFORMATION,
      COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_USERNAME
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.DIGITAL_BANKING_INFORMATION,
      COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_EMAIL_ADDRESS
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.DIGITAL_BANKING_INFORMATION,
      COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_PHONE_NUMBER
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.DIGITAL_BANKING_INFORMATION,
      COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_ROLE
    ),

    // Debit Card Information Complex Fields
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.DEBIT_CARD_INFORMATION,
      COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.DEBIT_CARD_NAME_DISPLAYED
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.DEBIT_CARD_INFORMATION,
      COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.BUSINESS_NAME_DISPLAYED_ON_CARD
    ),
  ];

  const bankerRequiredFields: string[] = [
    ...applicantRequiredFields,

    // Person Information Complex Fields
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.PERSON_INFORMATION,
      COMPLEX_FIELD_KEYS.PERSON_INFORMATION.DATE_OF_BIRTH
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.PERSON_INFORMATION,
      COMPLEX_FIELD_KEYS.PERSON_INFORMATION.SOCIAL_SECURITY_NUMBER
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.PERSON_INFORMATION,
      COMPLEX_FIELD_KEYS.PERSON_INFORMATION.HOME_ADDRESS
    ),

    // Government ID Information Complex Fields
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
      COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.IS_US_CITIZEN
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
      COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_TYPE
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
      COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_NUMBER
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
      COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_ISSUER_STATE
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
      COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_ISSUER_COUNTRY
    ),
    buildComplexFieldKey(
        FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
      COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_EXPIRY
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
      COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.IS_PHYSICAL_ADDRESS_DIFFERENT_THAN_ID_ADDRESS
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
      COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION
        .REASON_PHYSICAL_ADDRESS_IS_DIFFERENT_THAN_ID_ADDRESS
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
      COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION
        .OTHER_REASON_PHYSICAL_ADDRESS_IS_DIFFERENT_THAN_ID_ADDRESS
    ),

    // Employment Information Complex Fields
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.EMPLOYMENT_INFORMATION,
      COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.EMPLOYMENT_TYPE
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.EMPLOYMENT_INFORMATION,
      COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.EMPLOYER
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.EMPLOYMENT_INFORMATION,
      COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.JOB_TITLE
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.EMPLOYMENT_INFORMATION,
      COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.LENGTH_OF_EMPLOYMENT_YEARS
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.EMPLOYMENT_INFORMATION,
      COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.ATTORNEY_BAR_NUMBER
    ),

    // Security Questions Information Complex Fields
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.SECURITY_QUESTIONS_INFORMATION,
      COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_QUESTION_1
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.SECURITY_QUESTIONS_INFORMATION,
      COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_QUESTION_2
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.SECURITY_QUESTIONS_INFORMATION,
      COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_QUESTION_3
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.SECURITY_QUESTIONS_INFORMATION,
      COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_ANSWER_1
    ),
    buildComplexFieldKey(
        FIELD_KEYS.KEY_PERSON.SECURITY_QUESTIONS_INFORMATION,
      COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_ANSWER_2
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.SECURITY_QUESTIONS_INFORMATION,
      COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_ANSWER_3
    ),

    // Debit Card Information Complex Fields
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.DEBIT_CARD_INFORMATION,
      COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.USE_HOME_ADDRESS_FOR_DEBIT_CARD_MAILING_ADDRESS
    ),
    buildComplexFieldKey(
      FIELD_KEYS.KEY_PERSON.DEBIT_CARD_INFORMATION,
      COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.DEBIT_CARD_MAILING_ADDRESS
    ),
  ];

  const requiredFields =
    executionContextType === FinancialApplicationFormSectionContextType.Banker
      ? [...bankerRequiredFields]
      : executionContextType === FinancialApplicationFormSectionContextType.Applicant
        ? [...applicantRequiredFields]
        : executionContextType === FinancialApplicationFormSectionContextType.Task
          ? [...bankerRequiredFields]
          : [];

  return (onValueChange, form, errors) => {
    const fields = [
      ...schemaFactory.buildModelSchema<KeyPerson>(
        MODEL_KEYS.KEY_PERSON,
        onValueChange,
        form,
        errors,
        requiredFields,
        {
          PersonInformationExpressionSchema: PersonInformationComplexField,
          EmploymentInformationExpressionSchema: EmploymentInformationComplexField,
          DigitalBankingInformationExpressionSchema: DigitalBankingInformationComplexField,
          DebitCardInformationExpressionSchema: DebitCardInformationComplexField,
          GovernmentIdInformationExpressionSchema: GovernmentIdInformationComplexField,
          SecurityQuestionsInformationExpressionSchema: SecurityQuestionsInformationComplexField,
          IdentificationDocumentsExpressionSchema: IdentificationDocumentsComplexField,
        }
      ),
    ];

    return fields;
  };
};
